import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Stack,
  Typography,
  Container,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  LinearProgress
} from '@mui/material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { EntityContext } from '../entity';
import { AccountContext } from '../account';
import { createWebPKI, digestAlgForWebPki } from '../utils/webpki';

// RN-02/RN-04: mesmo teto de 20 min do ZIP (utils/batchZipDownload)
const JOB_POLL_MS = 5000;
const JOB_MAX_MS = 20 * 60 * 1000;
const JOB_STALL_MS = 10 * 60 * 1000;
const JOB_FINAL = ['completed', 'completed_with_failures', 'canceled', 'stalled'];

// RN-01: teto de assinaturas locais simultâneas no Web PKI; RN-03: fatia do envio; RN-04: linhas renderizadas
const SIGN_CONCURRENCY = 6;
const SEND_SLICE = 200;
const LIST_MAX = 100;
const MAX_ATTEMPTS = 3;

const limitar = async (itens, fn, limite = SIGN_CONCURRENCY) => {
  const out = new Array(itens.length);
  let proximo = 0;
  const worker = async () => {
    while (proximo < itens.length) {
      const i = proximo;
      proximo += 1;
      // pool: cada worker pega o próximo item só quando termina o atual
      // eslint-disable-next-line no-await-in-loop
      out[i] = await fn(itens[i]).then((v) => ({ ok: true, v }), (e) => ({ ok: false, e }));
    }
  };
  await Promise.all(Array.from({ length: Math.min(limite, itens.length) }, worker));
  return out;
};

const toToken = (i) => ({
  envelope_signer_key: i.envelope_signer_key,
  token: i.token,
  hash: i.to_sign_hash,
  alg: i.signature_algorithm,
});

const somar = (a, b) => (a ? {
  ...b,
  total_processed: a.total_processed + b.total_processed,
  successful_signatures: a.successful_signatures + b.successful_signatures,
  failed_signatures: a.failed_signatures + b.failed_signatures,
  finished_envelopes: a.finished_envelopes + b.finished_envelopes,
  results: [...(a.results || []), ...(b.results || [])],
} : b);

const CERT_DATE_FIELDS = ['validityEnd', 'validityEndDate', 'notAfter', 'validTo', 'expiresAt'];

const getCertificateEndDate = (cert) => {
  return CERT_DATE_FIELDS.reduce((foundDate, field) => {
    if (foundDate) return foundDate;
    const fieldValue = cert?.[field];
    if (!fieldValue) return null;
    const parsedDate = new Date(fieldValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }, null);
};

const isCertificateSelectable = (cert) => {
  if (!cert?.thumbprint) return false;
  if (cert?.isValid === false || cert?.isExpired === true) return false;
  const endDate = getCertificateEndDate(cert);
  if (endDate && endDate <= new Date()) return false;
  return true;
};

function BatchIcpSignatureConfirmationPage({ usuario = false }) {
  const { batchKey } = useParams();
  const navigate = useNavigate();
  const { getSession } = useContext(AccountContext);

  const entity = useContext(EntityContext);
  const { getBatchSignatureDetails } = entity;
  // US-019: `usuario` troca as rotas do lote de endosso pelas do lote do usuário logado
  const api = usuario
    ? {
        requestToken: entity.requestBatchSignatureIcpToken,
        send: entity.sendBatchSignatureIcpSignature,
        getJob: entity.getBatchSignatureIcpJob,
        getItems: entity.getBatchSignatureIcpJobItems,
        retry: entity.retryBatchSignatureIcpJob,
      }
    : {
        requestToken: entity.requestBatchEndorsementIcpToken,
        send: entity.sendBatchEndorsementIcpSignature,
        getJob: entity.getBatchIcpJob,
        getItems: entity.getBatchIcpJobItems,
        retry: entity.retryBatchIcpJob,
      };

  const [certificates, setCertificates] = useState([]);
  const [selectedCert, setSelectedCert] = useState('');
  const [loading, setLoading] = useState(true);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchTokens, setBatchTokens] = useState(null);
  const [signingProgress, setSigningProgress] = useState({
    isSigning: false,
    isGeneratingTokens: false,
    currentStep: 'idle',
    results: null
  });
  const [error, setError] = useState(null);
  const [bearerToken, setBearerToken] = useState(null);
  const [job, setJob] = useState(null);
  const [tokenJobId, setTokenJobId] = useState(null);
  const [falhas, setFalhas] = useState(null);
  const [tokensProntos, setTokensProntos] = useState(false);
  const [pendentes, setPendentes] = useState([]);
  const [assinados, setAssinados] = useState(0);
  const pendentesRef = useRef([]);
  pendentesRef.current = pendentes;

  const navigateToSignatureFinished = useCallback(() => {
    navigate(`/signature/finish`);
  }, [navigate]);

  useEffect(() => {
    if (!batchKey) return undefined;
    let cancelled = false;

    const initializePage = async () => {
      try {
        let jwt = null;
        try {
          const session = await getSession();
          jwt = session?.idToken?.jwtToken ?? null;
        } catch {
          jwt = null;
        }
        if (cancelled) return;
        setBearerToken(jwt);

        // lote do usuário: o detalhe do lote não é lido por esta rota
        const batchData = usuario ? {} : await getBatchSignatureDetails(jwt || undefined, batchKey);
        if (cancelled) return;

        if (batchData && batchData.error) {
          setError('Erro ao carregar detalhes do lote');
          setLoading(false);
          return;
        }
        setBatchDetails(batchData);

        const pki = createWebPKI();
        pki.init(() => {
          pki.listCertificates().success((certs) => {
            if (!cancelled) {
              const validCertificates = certs.filter(isCertificateSelectable);
              setCertificates(validCertificates);
              setLoading(false);
            }
          }).error(() => {
            if (!cancelled) {
              setError('Erro ao carregar certificados');
              setLoading(false);
            }
          });
        });
      } catch (err) {
        console.error('Erro na inicialização:', err);
        if (!cancelled) {
          setError('Erro na inicialização da página');
          setLoading(false);
        }
      }
    };

    initializePage();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getBatchSignatureDetails do contexto sem useCallback
  }, [batchKey, getSession]);

  const handleSelectCert = (event) => {
    setSelectedCert(event.target.value);
  };

  const fetchJobItems = async (jobId, params) => {
    const items = [];
    let cursor = 0;
    do {
      // keyset: cada página depende do next_cursor da anterior
      // eslint-disable-next-line no-await-in-loop
      const page = await api.getItems(jobId, batchKey, { ...params, cursor, limit: 1000 }, bearerToken);
      if (page?.error) throw Object.assign(new Error(page.error.message), { code: page.error.code });
      items.push(...page.items);
      cursor = page.next_cursor;
    } while (cursor != null);
    return items;
  };

  // RN-03: os tokens não são carregados de uma vez; rodarJob lê página a página
  const prontoParaAssinar = (jobId) => {
    setTokenJobId(jobId);
    setTokensProntos(true);
    setSigningProgress((prev) => ({ ...prev, currentStep: 'tokens_generated' }));
  };

  const loadJobFailures = async () => {
    try {
      setFalhas(await fetchJobItems(job.id, { status: 'failed' }));
    } catch (err) {
      setError(err.message);
    }
  };

  const startJob = (phase, id, total = 0) =>
    setJob({ phase, id, total, done: 0, failed: 0, status: 'active' });

  // RN-05: 409 devolve o job existente; sign_job_id tem prioridade sobre token_job_id
  const resumeJob = ({ tokenJobId: tokenId, signJobId }) => {
    if (tokenId) setTokenJobId(tokenId);
    if (signJobId) startJob('sign', signJobId);
    else if (tokenId) startJob('token', tokenId);
    else throw new Error('Já existe um processamento em andamento para este lote.');
  };

  const jobActive = job?.status === 'active';
  // o job de assinatura só termina quando recebe os tokens ainda não enviados: só o de token bloqueia o envio
  const tokenJobActive = jobActive && job.phase === 'token';

  useEffect(() => {
    if (!jobActive) return undefined;
    let cancelled = false;
    let timer;
    const startedAt = Date.now();
    let lastCount = job.done + job.failed;
    let lastAdvance = startedAt;

    const tick = async () => {
      const r = await api.getJob(job.id, batchKey, bearerToken);
      if (cancelled) return;
      const now = Date.now();
      if (r?.error) {
        // rede ou 5xx isolado (ex.: 502 do ALB): segue consultando até o prazo de parado; 4xx para na hora
        const transitorio = !r.error.status || r.error.status >= 500;
        if (transitorio && now - lastAdvance < JOB_STALL_MS && now - startedAt < JOB_MAX_MS) {
          timer = setTimeout(tick, JOB_POLL_MS);
          return;
        }
        setError(r.error.message);
        setJob((prev) => ({ ...prev, status: 'stalled' }));
        return;
      }
      const count = r.done + r.failed;
      if (count > lastCount) {
        lastCount = count;
        lastAdvance = now;
      }
      const parou =
        !JOB_FINAL.includes(r.status) && (now - lastAdvance >= JOB_STALL_MS || now - startedAt >= JOB_MAX_MS);
      const status = parou ? 'stalled' : r.status;
      setJob((prev) => ({ ...prev, ...r, status }));
      if (JOB_FINAL.includes(status)) {
        if (job.phase === 'token' && status.startsWith('completed')) prontoParaAssinar(job.id);
        if (job.phase === 'sign' && status === 'completed' && !pendentesRef.current.length) setTimeout(navigateToSignatureFinished, 3000);
        return;
      }
      timer = setTimeout(tick, JOB_POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reinicia só quando o job (id/fase) ou o estado ativo muda
  }, [job?.id, job?.phase, jobActive]);

  const enviarAssinaturas = (signatures) =>
    api.send(batchKey, signatures, bearerToken, tokenJobId);

  // RN-01/RN-02: assina com no máximo 6 simultâneas; falha local deixa só o documento em `pendentes`
  const assinarLocal = async (pki, tokens) => {
    await new Promise((resolve) => {
      pki.init(() => resolve());
    });
    const out = await limitar(tokens, (t) => new Promise((resolve, reject) => {
      pki.signHash({
        thumbprint: selectedCert,
        hash: t.hash,
        digestAlgorithm: digestAlgForWebPki(t.alg),
      }).success(resolve).error(reject);
    }));
    const signatures = [];
    const locais = [];
    out.forEach((o, i) => {
      if (o.ok) signatures.push({ envelope_signer_key: tokens[i].envelope_signer_key, signature: o.v });
      else locais.push(tokens[i]);
    });
    return { signatures, locais };
  };

  /** Caminho síncrono: assina os tokens e envia numa chamada só */
  const runBatchIcpSign = async (pki, tokensPayload) => {
    if (!tokensPayload?.tokens?.length) {
      throw new Error('Sem tokens para assinar');
    }
    if (!selectedCert) {
      throw new Error('Selecione um certificado');
    }

    const { signatures, locais } = await assinarLocal(pki, tokensPayload.tokens);
    if (!signatures.length) {
      throw new Error('Não foi possível assinar nenhum documento');
    }

    const result = await enviarAssinaturas(signatures);
    if (!result) {
      throw new Error('Erro ao finalizar assinaturas em lote');
    }
    return { result, locais };
  };

  const applySignResult = ({ result, locais }, anterior = null) => {
    setPendentes(locais);
    setSigningProgress((prev) => ({
      ...prev,
      isSigning: false,
      currentStep: 'completed',
      results: somar(anterior, result),
    }));
    if (!locais.length && result.successful_signatures === result.total_processed) {
      setTimeout(() => {
        navigateToSignatureFinished();
      }, 3000);
    }
  };

  /** Caminho de job: assina e envia em fatias de 200; devolve o que ficou pendente (local ou rejeitado) */
  const assinarEEnviar = async (pki, tokens) => {
    const { signatures, locais } = await assinarLocal(pki, tokens);
    const porKey = Object.fromEntries(tokens.map((t) => [t.envelope_signer_key, t]));
    const novos = [...locais];
    let aceitas = 0;
    let ultimo = null;
    let erro = null;
    for (let i = 0; i < signatures.length; i += SEND_SLICE) {
      const fatia = signatures.slice(i, i + SEND_SLICE);
      // fatias em sequência: o manager recebe uma por vez
      // eslint-disable-next-line no-await-in-loop
      const r = await enviarAssinaturas(fatia);
      if (!r || r.error) {
        erro = r?.error?.message || 'Erro ao enviar assinaturas';
        novos.push(...signatures.slice(i).map((sig) => porKey[sig.envelope_signer_key]));
        break;
      }
      aceitas += r.accepted?.length ?? 0;
      novos.push(...(r.rejected || []).map((k) => porKey[k]).filter(Boolean));
      ultimo = r;
    }
    return { pendentes: novos, aceitas, ultimo, erro };
  };

  // RN-03: lê os tokens do job em páginas, assina e envia página a página; `reassinar` refaz só os pendentes
  const rodarJob = async (reassinar = false) => {
    if (!selectedCert) return;
    setError(null);
    setSigningProgress((prev) => ({ ...prev, isSigning: true, currentStep: 'signing' }));
    const pend = [];
    let aceitas = reassinar ? assinados : 0;
    let ultimo = null;
    let erro = null;
    try {
      const pki = createWebPKI();
      let cursor = 0;
      do {
        let tokens = pendentes;
        if (reassinar) {
          cursor = null;
        } else {
          // keyset: cada página depende do next_cursor da anterior
          // eslint-disable-next-line no-await-in-loop
          const page = await api.getItems(tokenJobId, batchKey, { cursor, limit: 1000 }, bearerToken);
          if (page?.error) throw new Error(page.error.message);
          tokens = page.items.map(toToken);
          cursor = page.next_cursor;
        }
        // eslint-disable-next-line no-await-in-loop
        const r = await assinarEEnviar(pki, tokens);
        pend.push(...r.pendentes);
        aceitas += r.aceitas;
        ultimo = r.ultimo || ultimo;
        setAssinados(aceitas);
        setPendentes([...pend]);
        if (r.erro) {
          ({ erro } = r);
          break;
        }
      } while (cursor != null);
    } catch (err) {
      erro = err.message;
    }
    if (erro) setError(`Erro ao assinar documentos: ${erro}`);
    setSigningProgress((prev) => ({
      ...prev,
      isSigning: false,
      currentStep: erro || pend.length ? 'tokens_generated' : 'completed',
    }));
    if (ultimo) startJob('sign', ultimo.job_id, ultimo.total);
  };

  // RN-05: o certificado já escolhido vira o pedido de reprocesso; o novo job é de token
  const reprocessarFalhas = async () => {
    if (!selectedCert) return;
    try {
      setError(null);
      const pki = createWebPKI();
      const certEncoding = await new Promise((resolve, reject) => {
        pki.readCertificate({ thumbprint: selectedCert }).success(resolve).error(reject);
      });
      const r = await api.retry(job.id, batchKey, certEncoding, bearerToken);
      if (r?.error?.code === 'ICP_JOB_IN_PROGRESS') {
        resumeJob(r.error);
        return;
      }
      if (r?.error) throw new Error(r.error.message);
      setFalhas(null);
      setPendentes([]);
      setAssinados(0);
      setTokensProntos(false);
      setTokenJobId(r.job_id);
      setSigningProgress((prev) => ({ ...prev, currentStep: 'idle' }));
      startJob('token', r.job_id, r.total);
    } catch (err) {
      setError(`Erro ao reprocessar falhas: ${err.message}`);
    }
  };

  const generateBatchTokens = async () => {
    if (!selectedCert || !batchDetails) return;

    let tokensReceived = false;
    let tokenRequestFailed = false;
    try {
      setSigningProgress((prev) => ({
        ...prev,
        isGeneratingTokens: true,
        currentStep: 'generating_tokens',
      }));
      setError(null);

      const pki = createWebPKI();
      const certEncoding = await new Promise((resolve, reject) => {
        pki.readCertificate({ thumbprint: selectedCert }).success(resolve).error(reject);
      });

      const tokensData = await api.requestToken(batchKey, certEncoding, bearerToken);

      if (tokensData?.error?.code === 'ICP_JOB_IN_PROGRESS') {
        resumeJob(tokensData.error);
        setSigningProgress((prev) => ({ ...prev, isGeneratingTokens: false, currentStep: 'idle' }));
        return;
      }

      if (tokensData?.error?.message) {
        // RN-08/RN-09: a message do backend (inclusive o teto ICP_BATCH_LIMIT_EXCEEDED) e o code seguem até a tela
        tokenRequestFailed = true;
        throw Object.assign(new Error(tokensData.error.message), { code: tokensData.error.code });
      }

      // RN-01: 202 com job = caminho assíncrono; lista = fluxo síncrono de sempre
      if (tokensData?.job_id) {
        startJob('token', tokensData.job_id, tokensData.total);
        setSigningProgress((prev) => ({ ...prev, isGeneratingTokens: false, currentStep: 'idle' }));
        return;
      }

      // RN-02 (US-019): lote do usuário não tem caminho síncrono
      if (usuario) {
        tokenRequestFailed = true;
        throw new Error('Erro ao gerar tokens em lote');
      }

      if (!tokensData?.tokens?.length) {
        throw new Error('Erro ao gerar tokens em lote');
      }

      setSigningProgress((prev) => ({
        ...prev,
        isGeneratingTokens: false,
        currentStep: 'tokens_generated',
      }));

      setSigningProgress((prev) => ({ ...prev, isSigning: true, currentStep: 'signing' }));
      setError(null);

      let signed;
      try {
        signed = await runBatchIcpSign(pki, tokensData);
      } catch (signErr) {
        setBatchTokens(tokensData);
        tokensReceived = true;
        setSigningProgress((prev) => ({
          ...prev,
          isSigning: false,
          currentStep: 'tokens_generated',
        }));
        throw signErr;
      }

      setBatchTokens(tokensData);
      tokensReceived = true;
      applySignResult(signed);
    } catch (err) {
      console.error('Erro no fluxo ICP em lote:', err);
      setError(
        tokenRequestFailed
          ? err.message
          : err.message?.includes('assinar') || err.message?.includes('finalizar')
          ? `Erro ao assinar documentos: ${err.message || 'Erro desconhecido'}`
          : `Erro ao gerar tokens em lote: ${err.message || 'Erro desconhecido'}`
      );
      setSigningProgress((prev) => ({
        ...prev,
        isGeneratingTokens: false,
        isSigning: false,
        currentStep: tokensReceived ? 'tokens_generated' : 'idle',
      }));
    }
  };

  const signBatchDocuments = async () => {
    if (!batchTokens) return;

    try {
      setSigningProgress((prev) => ({
        ...prev,
        isSigning: true,
        currentStep: 'signing',
      }));
      setError(null);

      const pki = createWebPKI();
      applySignResult(await runBatchIcpSign(pki, batchTokens));
    } catch (err) {
      console.error('Erro ao assinar documentos:', err);
      setError(`Erro ao assinar documentos: ${err.message || 'Erro desconhecido'}`);
      setSigningProgress((prev) => ({
        ...prev,
        isSigning: false,
        currentStep: 'tokens_generated',
      }));
    }
  };

  // RN-02: reassina só os pendentes e soma ao resultado já recebido
  const reassinarPendentes = async () => {
    if (tokensProntos) return rodarJob(true);
    try {
      setSigningProgress((prev) => ({ ...prev, isSigning: true, currentStep: 'signing' }));
      setError(null);
      const signed = await runBatchIcpSign(createWebPKI(), { tokens: pendentes });
      applySignResult(signed, signingProgress.results);
    } catch (err) {
      setError(`Erro ao assinar documentos: ${err.message || 'Erro desconhecido'}`);
      setSigningProgress((prev) => ({ ...prev, isSigning: false, currentStep: 'tokens_generated' }));
    }
    return undefined;
  };

  const getStatusIcon = (envelopeSignerKey) => {
    if (!signingProgress.results) return <PendingIcon color="disabled" />;

    const result = signingProgress.results.results?.find(r => r.envelope_signer_key === envelopeSignerKey);
    if (!result) return <PendingIcon color="disabled" />;

    if (result.success) {
      return <CheckCircleIcon color="success" />;
    }
    return <ErrorIcon color="error" />;
  };

  const getStatusText = (envelopeSignerKey) => {
    if (!signingProgress.results) return 'Pendente';

    const result = signingProgress.results.results?.find(r => r.envelope_signer_key === envelopeSignerKey);
    if (!result) return 'Pendente';

    if (result.success) {
      return 'Assinado';
    }
    return result.error || 'Falhou';
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ mt: { xs: 2, sm: 4 }, mb: 8 }}>
          <CircularProgress />
          <Typography>Carregando...</Typography>
        </Stack>
      </Container>
    );
  }

  if (error && !batchDetails) {
    return (
      <Container maxWidth="md">
        <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ mt: 8, mb: 8 }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Tentar Novamente
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Assinatura ICP em Lote</title>
      </Helmet>

      <Container maxWidth="md">
        <Stack spacing={4} sx={{ mt: { xs: 1, sm: 2 }, mb: 4 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ px: { xs: 0.5, sm: 0 } }}>
            Assinatura ICP em Lote
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {batchDetails && !bearerToken && (
            <Alert severity="warning">
              É necessário estar logado para gerar tokens e concluir a assinatura.
            </Alert>
          )}

          {batchDetails && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detalhes do Lote
                </Typography>
                {!usuario && (
                  <Typography variant="body2" color="text.secondary">
                    Total de envelopes:{' '}
                    {batchDetails.envelopes?.length ||
                      batchDetails.envelope_signer_keys?.length ||
                      0}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Chave do lote: {batchKey}
                </Typography>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Selecione um Certificado
              </Typography>

              <FormControl fullWidth>
                <InputLabel id="cert-select-label">Certificado</InputLabel>
                <Select
                  labelId="cert-select-label"
                  id="cert-select"
                  value={selectedCert}
                  label="Certificado"
                  onChange={handleSelectCert}
                  disabled={signingProgress.isGeneratingTokens || signingProgress.isSigning || jobActive}
                >
                  <MenuItem value="">Selecione um certificado</MenuItem>
                  {certificates.map((cert) => (
                    <MenuItem key={cert.thumbprint} value={cert.thumbprint}>
                      {cert.subjectName} (emitido por {cert.issuerName})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!certificates.length && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Nenhum certificado valido e nao expirado foi encontrado.
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                {!batchTokens && !tokensProntos ? (
                  <Button
                    variant="contained"
                    onClick={generateBatchTokens}
                    disabled={
                      !selectedCert ||
                      jobActive ||
                      signingProgress.isGeneratingTokens ||
                      signingProgress.isSigning ||
                      signingProgress.currentStep === 'completed'
                    }
                    fullWidth
                    startIcon={
                      signingProgress.isGeneratingTokens || signingProgress.isSigning ? (
                        <CircularProgress size={20} />
                      ) : (
                        <PlayArrowIcon />
                      )
                    }
                  >
                    {signingProgress.isGeneratingTokens
                      ? 'Gerando tokens...'
                      : signingProgress.isSigning
                        ? 'Assinando documentos...'
                        : 'Assinar'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={batchTokens ? signBatchDocuments : () => rodarJob(false)}
                    disabled={
                      tokenJobActive || signingProgress.isSigning || signingProgress.currentStep === 'completed'
                    }
                    fullWidth
                    startIcon={signingProgress.isSigning ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                  >
                    {signingProgress.isSigning ? 'Assinando...' : batchTokens ? 'Tentar assinar novamente' : 'Assinar documentos'}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>

          {(pendentes.length > 0 || assinados > 0) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Documentos
                </Typography>
                {tokensProntos && <Typography variant="body2">{assinados} enviados para assinatura</Typography>}
                <Typography variant="body2" color={pendentes.length ? 'error' : 'text.secondary'}>
                  {pendentes.length} pendentes
                </Typography>
                {pendentes.length > 0 && (
                  <>
                    <List dense>
                      {pendentes.slice(0, LIST_MAX).map((t) => (
                        <ListItem key={t.envelope_signer_key}>
                          <ListItemText primary={t.envelope_signer_key} secondary="Pendente" />
                        </ListItem>
                      ))}
                    </List>
                    {pendentes.length > LIST_MAX && (
                      <Typography variant="caption">Mostrando {LIST_MAX} de {pendentes.length}</Typography>
                    )}
                    <Button
                      sx={{ mt: 1 }}
                      variant="outlined"
                      onClick={reassinarPendentes}
                      disabled={signingProgress.isSigning || tokenJobActive}
                    >
                      Reassinar pendentes
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {job && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {job.phase === 'token' ? 'Gerando Tokens' : 'Assinando Documentos'}
                </Typography>
                <Typography>
                  {job.done} de {job.total} documentos
                </Typography>
                {job.failed > 0 && (
                  <Typography color="error">{job.failed} com falha</Typography>
                )}
                {job.total > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, ((job.done + job.failed) / job.total) * 100)}
                    sx={{ mt: 2 }}
                  />
                )}
                {job.status === 'completed' && (
                  <Alert severity="success" sx={{ mt: 2 }}>Concluído.</Alert>
                )}
                {job.status === 'completed_with_failures' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Concluído com falhas.
                    <Button size="small" onClick={loadJobFailures}>Ver falhas</Button>
                  </Alert>
                )}
                {job.status === 'canceled' && (
                  <Alert severity="info" sx={{ mt: 2 }}>Processamento cancelado.</Alert>
                )}
                {job.status === 'stalled' && (
                  <Alert
                    severity="warning"
                    sx={{ mt: 2 }}
                    action={<Button size="small" onClick={generateBatchTokens} disabled={!selectedCert}>Tentar novamente</Button>}
                  >
                    O processamento parou. Tente novamente para retomar.
                  </Alert>
                )}
                {job.phase === 'sign' && tokenJobId && !batchTokens && (
                  <Button sx={{ mt: 2 }} onClick={() => prontoParaAssinar(tokenJobId)}>
                    Assinar tokens pendentes
                  </Button>
                )}
                {falhas && (
                  <>
                    <Typography sx={{ mt: 2 }}>{falhas.length} com falha</Typography>
                    <List dense>
                      {[...falhas].sort((x, y) => (y.attempts >= MAX_ATTEMPTS) - (x.attempts >= MAX_ATTEMPTS)).slice(0, LIST_MAX).map((f) => (
                        <ListItem key={f.envelope_signer_key}>
                          <ListItemText primary={f.envelope_signer_key} secondary={f.error_code} />
                          {f.attempts >= MAX_ATTEMPTS && <Chip label="Esgotado" color="error" size="small" />}
                        </ListItem>
                      ))}
                    </List>
                    {falhas.length > LIST_MAX && (
                      <Typography variant="caption">Mostrando {LIST_MAX} de {falhas.length}</Typography>
                    )}
                    {falhas.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        O lote só fecha depois que todas as falhas forem resolvidas.
                      </Alert>
                    )}
                    {falhas.some((f) => f.attempts >= MAX_ATTEMPTS) && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        Há itens esgotados, sem reprocesso: o lote continua aberto até a engenharia ajustar o dado.
                      </Alert>
                    )}
                    {falhas.some((f) => f.attempts < MAX_ATTEMPTS) && (
                      <Button sx={{ mt: 2 }} variant="outlined" onClick={reprocessarFalhas} disabled={!selectedCert}>
                        Reprocessar falhas
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {signingProgress.isGeneratingTokens && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Gerando Tokens
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography>Gerando tokens para todos os envelopes...</Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {signingProgress.isSigning && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Assinando Documentos
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography>Assinando todos os documentos com certificado digital...</Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {signingProgress.results && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resultados da Assinatura
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total processado: {signingProgress.results.total_processed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assinaturas bem-sucedidas: {signingProgress.results.successful_signatures}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assinaturas que falharam: {signingProgress.results.failed_signatures}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Envelopes finalizados: {signingProgress.results.finished_envelopes}
                  </Typography>
                </Box>

                <List dense>
                  {signingProgress.results.results?.slice(0, LIST_MAX).map((result, index) => (
                    <div key={result.envelope_signer_key}>
                      <ListItem>
                        <ListItemIcon>
                          {getStatusIcon(result.envelope_signer_key)}
                        </ListItemIcon>
                        <ListItemText
                          primary={`Envelope ${result.envelope_key}`}
                          secondary={getStatusText(result.envelope_signer_key)}
                        />
                        {result.success && (
                          <Chip
                            label={result.envelope_finished ? "Finalizado" : "Assinado"}
                            color={result.envelope_finished ? "success" : "primary"}
                            size="small"
                          />
                        )}
                        {!result.success && (
                          <Chip label="Falhou" color="error" size="small" />
                        )}
                      </ListItem>
                      {index < (signingProgress.results.results?.length || 0) - 1 && <Divider />}
                    </div>
                  ))}
                </List>
                {signingProgress.results.results?.length > LIST_MAX && (
                  <Typography variant="caption">
                    Mostrando {LIST_MAX} de {signingProgress.results.results.length}
                  </Typography>
                )}

                {!pendentes.length && signingProgress.results.successful_signatures === signingProgress.results.total_processed && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Todas as assinaturas foram realizadas com sucesso! Redirecionando...
                  </Alert>
                )}

                {signingProgress.results.failed_signatures > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {signingProgress.results.failed_signatures} assinatura(s) falharam. Verifique os detalhes acima.
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </Stack>
      </Container>
    </>
  );
}

export default BatchIcpSignatureConfirmationPage;
